import React, { useEffect, useState, useRef } from 'react';
import { Button, Input, Table } from 'reactstrap';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4maps from '@amcharts/amcharts4/maps';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import cities from './mock';
import roteNdaoGeo from '../../assets/jsonData/kab_Rote.json';
import TulufaliLogo from '../Icons/tulufali_logo.svg';
import s from './am4chartMap.module.scss';
import './tooltip.css';
import { GET_REKAP_DTSEN_DESA } from '../../utils';
import { numberWithCommas } from '../../utils/func';
import axios from 'axios';
import { API_TIMEOUT } from '../../utils';

const TAGLINE = 'Tata Kelola Bantuan Terintegrasi Untuk Penanggulangan Kemiskinan Rote Ndao';

/** Normalisasi nama desa/kecamatan: trim dulu, lalu uppercase. Untuk pencocokan dengan peta. */
const normalizeForMatch = (str) => String(str || '').trim().toUpperCase();

const DESIL_OPTIONS = [
  { value: 1, label: 'Desil 1 (Sangat Miskin)' },
  { value: 2, label: 'Desil 2 (Miskin)' },
  { value: 3, label: 'Desil 3 (Rentan Miskin)' },
  { value: 4, label: 'Desil 4 (Hampir Rentan)' },
  { value: 5, label: 'Desil 5 (Menengah Bawah)' },
  { value: 6, label: 'Desil 6–10 (Menengah Atas)' },
  { value: 7, label: 'Persen Desil 1 (thd total penduduk)' },
  { value: 8, label: 'Persen Desil 1+2 (thd total penduduk)' },
];

/* Gradasi 4 kelompok: muda (sedikit) → tua (terbanyak) */
const DESIL_COLORS = {
  none: '#e8e8e8',
  low: '#90caf9',    /* kelompok 1 - paling sedikit */
  medium: '#42a5f5', /* kelompok 2 */
  high: '#1976d2',   /* kelompok 3 */
  highest: '#0d47a1', /* kelompok 4 - terbanyak */
};

const Am4chartMapDesil = () => {
  const [listDesilDesa, setListDesilDesa] = useState([]);
  const [selectedDesil, setSelectedDesil] = useState(1);
  const [isLoadingMap, setIsLoadingMap] = useState(false);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const mapRef = useRef(null);
  const mapWrapRef = useRef(null);
  const [mapCombine, setMapCombine] = useState();
  const [thresholds, setThresholds] = useState({ t1: 0, t2: 0, t3: 0, isPercentMode: false });
  const features = roteNdaoGeo.features;

  const getListDesilDesa = async () => {
    setIsLoadingMap(true);
    try {
      const res = await axios.get(GET_REKAP_DTSEN_DESA, { timeout: API_TIMEOUT });
      const data = res.data?.data || [];
      setListDesilDesa(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('getListDesilDesa:', err);
      setListDesilDesa([]);
    } finally {
      setIsLoadingMap(false);
    }
  };

  useEffect(() => {
    getListDesilDesa();
  }, []);

  useEffect(() => {
    if (!listDesilDesa.length) return;

    const isPercentMode = selectedDesil === 7 || selectedDesil === 8;
    const desilKey = selectedDesil === 6 ? 'Desil_6_10' : selectedDesil <= 6 ? `Desil_${selectedDesil}` : null;

    const desaLookup = new Map();
    listDesilDesa.forEach((item) => {
      const key = normalizeForMatch(item?.desa);
      if (key) desaLookup.set(key, item);
    });

    const getValueForDesa = (row) => {
      if (!row) return 0;
      if (isPercentMode) {
        const total = Number(row.jumlahOrang) || 0;
        if (total <= 0) return 0;
        const num = selectedDesil === 7
          ? Number(row.Desil_1) || 0
          : (Number(row.Desil_1) || 0) + (Number(row.Desil_2) || 0);
        return (num / total) * 100;
      }
      return Number(row[desilKey]) || 0;
    };

    const values = listDesilDesa
      .map((d) => getValueForDesa(d))
      .filter((v) => v > 0)
      .sort((a, b) => a - b);

    let t1 = 0,
      t2 = 0,
      t3 = 0;
    if (values.length >= 4) {
      const q1 = Math.floor(values.length / 4);
      const q2 = Math.floor((2 * values.length) / 4);
      const q3 = Math.floor((3 * values.length) / 4);
      t1 = values[q1] || 0;
      t2 = values[q2] || 0;
      t3 = values[q3] || 0;
    } else if (values.length === 3) {
      t1 = values[0];
      t2 = values[1];
      t3 = values[2];
    } else if (values.length === 2) {
      t1 = t2 = values[0];
      t3 = values[1];
    } else if (values.length === 1) {
      t1 = t2 = t3 = values[0];
    }
    setThresholds({ t1, t2, t3, isPercentMode });

    const getTierColor = (val) => {
      if (val == null || val <= 0) return DESIL_COLORS.none;
      if (val <= t1) return DESIL_COLORS.low;
      if (val <= t2) return DESIL_COLORS.medium;
      if (val <= t3) return DESIL_COLORS.high;
      return DESIL_COLORS.highest;
    };

    const desaku = [];
    for (let i = 0; i < features.length; i++) {
      const desa = features[i];
      const lookupKey = normalizeForMatch(desa.properties?.DESA);
      const row = desaLookup.get(lookupKey);
      const count = getValueForDesa(row);

      desa.properties.penerimaDesil = isPercentMode ? Number(count.toFixed(1)) : count;
      if (isPercentMode) {
        if (row) {
          const totKK = Number(row.jumlahKK) || 0;
          desa.properties.penerimaDesilKK = totKK > 0
            ? (selectedDesil === 7
              ? ((Number(row.Desil_1_KK) || 0) / totKK) * 100
              : ((Number(row.Desil_1_KK) || 0) + (Number(row.Desil_2_KK) || 0)) / totKK * 100
            ).toFixed(1)
            : '0';
        } else {
          desa.properties.penerimaDesilKK = '0';
        }
      } else {
        desa.properties.penerimaDesilKK = '0';
      }
      desa.properties.jumlahOrang = row ? Number(row.jumlahOrang) || 0 : 0;
      desa.properties.jumlahKK = row ? Number(row.jumlahKK) || 0 : 0;
      desa.properties.desil1 = row ? Number(row.Desil_1) || 0 : 0;
      desa.properties.desil2 = row ? Number(row.Desil_2) || 0 : 0;
      desa.properties.desil3 = row ? Number(row.Desil_3) || 0 : 0;
      desa.properties.desil4 = row ? Number(row.Desil_4) || 0 : 0;
      desa.properties.desil5 = row ? Number(row.Desil_5) || 0 : 0;
      desa.properties.desil610 = row ? Number(row.Desil_6_10) || 0 : 0;
      desa.properties.desil1KK = row ? Number(row.Desil_1_KK) || 0 : 0;
      desa.properties.desil2KK = row ? Number(row.Desil_2_KK) || 0 : 0;
      desa.properties.desil3KK = row ? Number(row.Desil_3_KK) || 0 : 0;
      desa.properties.desil4KK = row ? Number(row.Desil_4_KK) || 0 : 0;
      desa.properties.desil5KK = row ? Number(row.Desil_5_KK) || 0 : 0;
      desa.properties.desil610KK = row ? Number(row.Desil_6_10_KK) || 0 : 0;
      const totOrang = desa.properties.jumlahOrang;
      const totKK = desa.properties.jumlahKK;
      const fmt = (n, d) => (d > 0 ? ((n / d) * 100).toFixed(1) : '0');
      desa.properties.pctDesil1 = fmt(desa.properties.desil1, totOrang);
      desa.properties.pctDesil2 = fmt(desa.properties.desil2, totOrang);
      desa.properties.pctDesil3 = fmt(desa.properties.desil3, totOrang);
      desa.properties.pctDesil4 = fmt(desa.properties.desil4, totOrang);
      desa.properties.pctDesil5 = fmt(desa.properties.desil5, totOrang);
      desa.properties.pctDesil610 = fmt(desa.properties.desil610, totOrang);
      desa.properties.pctDesil1KK = fmt(desa.properties.desil1KK, totKK);
      desa.properties.pctDesil2KK = fmt(desa.properties.desil2KK, totKK);
      desa.properties.pctDesil3KK = fmt(desa.properties.desil3KK, totKK);
      desa.properties.pctDesil4KK = fmt(desa.properties.desil4KK, totKK);
      desa.properties.pctDesil5KK = fmt(desa.properties.desil5KK, totKK);
      desa.properties.pctDesil610KK = fmt(desa.properties.desil610KK, totKK);
      desa.properties.color = getTierColor(count);
      desaku.push(desa);
    }

    setMapCombine({
      type: 'FeatureCollection',
      features: desaku,
    });
  }, [listDesilDesa, selectedDesil]);

  useEffect(() => {
    if (!mapCombine) return;

    if (mapRef.current) {
      mapRef.current.dispose();
      mapRef.current = null;
    }

    const map = am4core.create('mapDesil', am4maps.MapChart);
    mapRef.current = map;
    if (map.logo) map.logo.hide();
    setTimeout(() => {
      if (map.logo) map.logo.hide();
    }, 500);
    map.maxZoomLevel = 4;
    map.maxPanOut = 0.75;
    map.geodata = mapCombine;
    map.percentHeight = 90;
    map.dy = 10;

    const polygonSeries = map.series.push(new am4maps.MapPolygonSeries());
    polygonSeries.useGeodata = true;
    map.homeZoomLevel = 1;
    map.zoomControl = new am4maps.ZoomControl();
    map.zoomControl.layout = 'horizontal';
    map.zoomControl.align = 'left';
    map.zoomControl.valign = 'bottom';
    map.zoomControl.dy = 30;
    map.zoomControl.dx = 30;
    map.zoomControl.contentHeight = 20;
    map.zoomControl.minusButton.background.fill = am4core.color('#C7D0FF');
    map.zoomControl.minusButton.background.stroke = am4core.color('white');
    map.zoomControl.minusButton.label.fontWeight = 600;
    map.zoomControl.minusButton.label.fontSize = 22;
    map.zoomControl.minusButton.scale = 0.75;
    map.zoomControl.minusButton.label.scale = 0.75;
    map.zoomControl.plusButton.background.fill = am4core.color('#C7D0FF');
    map.zoomControl.plusButton.background.stroke = am4core.color('#6979C9');
    map.zoomControl.plusButton.label.fontWeight = 600;
    map.zoomControl.plusButton.label.fontSize = 22;
    map.zoomControl.plusButton.label.align = 'center';
    map.zoomControl.plusButton.scale = 0.75;
    map.zoomControl.plusButton.label.scale = 0.75;
    map.zoomControl.plusButton.dx = 5;
    const plusHover = map.zoomControl.plusButton.background.states.create('hover');
    plusHover.properties.fill = am4core.color('#354D84');
    const minusHover = map.zoomControl.minusButton.background.states.create('hover');
    minusHover.properties.fill = am4core.color('#354D84');

    const polygonTemplate = polygonSeries.mapPolygons.template;
    const tooltipRows = {
      1: () => `<div class="map-tooltip-row map-tooltip-highlight"><span class="map-tooltip-label">Desil 1</span></div><div class="map-tooltip-row"><span class="map-tooltip-label">Individu</span><span class="map-tooltip-value">{desil1} org ({pctDesil1}%)</span></div><div class="map-tooltip-row"><span class="map-tooltip-label">KK</span><span class="map-tooltip-value">{desil1KK} KK ({pctDesil1KK}%)</span></div>`,
      2: () => `<div class="map-tooltip-row map-tooltip-highlight"><span class="map-tooltip-label">Desil 2</span></div><div class="map-tooltip-row"><span class="map-tooltip-label">Individu</span><span class="map-tooltip-value">{desil2} org ({pctDesil2}%)</span></div><div class="map-tooltip-row"><span class="map-tooltip-label">KK</span><span class="map-tooltip-value">{desil2KK} KK ({pctDesil2KK}%)</span></div>`,
      3: () => `<div class="map-tooltip-row map-tooltip-highlight"><span class="map-tooltip-label">Desil 3</span></div><div class="map-tooltip-row"><span class="map-tooltip-label">Individu</span><span class="map-tooltip-value">{desil3} org ({pctDesil3}%)</span></div><div class="map-tooltip-row"><span class="map-tooltip-label">KK</span><span class="map-tooltip-value">{desil3KK} KK ({pctDesil3KK}%)</span></div>`,
      4: () => `<div class="map-tooltip-row map-tooltip-highlight"><span class="map-tooltip-label">Desil 4</span></div><div class="map-tooltip-row"><span class="map-tooltip-label">Individu</span><span class="map-tooltip-value">{desil4} org ({pctDesil4}%)</span></div><div class="map-tooltip-row"><span class="map-tooltip-label">KK</span><span class="map-tooltip-value">{desil4KK} KK ({pctDesil4KK}%)</span></div>`,
      5: () => `<div class="map-tooltip-row map-tooltip-highlight"><span class="map-tooltip-label">Desil 5</span></div><div class="map-tooltip-row"><span class="map-tooltip-label">Individu</span><span class="map-tooltip-value">{desil5} org ({pctDesil5}%)</span></div><div class="map-tooltip-row"><span class="map-tooltip-label">KK</span><span class="map-tooltip-value">{desil5KK} KK ({pctDesil5KK}%)</span></div>`,
      6: () => `<div class="map-tooltip-row map-tooltip-highlight"><span class="map-tooltip-label">Desil 6–10</span></div><div class="map-tooltip-row"><span class="map-tooltip-label">Individu</span><span class="map-tooltip-value">{desil610} org ({pctDesil610}%)</span></div><div class="map-tooltip-row"><span class="map-tooltip-label">KK</span><span class="map-tooltip-value">{desil610KK} KK ({pctDesil610KK}%)</span></div>`,
      7: () => `<div class="map-tooltip-row map-tooltip-highlight"><span class="map-tooltip-label">Persen Desil 1</span></div><div class="map-tooltip-row"><span class="map-tooltip-label">Individu</span><span class="map-tooltip-value">{penerimaDesil}%</span></div><div class="map-tooltip-row"><span class="map-tooltip-label">KK</span><span class="map-tooltip-value">{penerimaDesilKK}%</span></div>`,
      8: () => `<div class="map-tooltip-row map-tooltip-highlight"><span class="map-tooltip-label">Persen Desil 1+2</span></div><div class="map-tooltip-row"><span class="map-tooltip-label">Individu</span><span class="map-tooltip-value">{penerimaDesil}%</span></div><div class="map-tooltip-row"><span class="map-tooltip-label">KK</span><span class="map-tooltip-value">{penerimaDesilKK}%</span></div>`,
    };
    const selectedRow = tooltipRows[selectedDesil] ? tooltipRows[selectedDesil]() : tooltipRows[1]();
    polygonTemplate.tooltipHTML = `
    <div class="map-tooltip-card">
      <div class="map-tooltip-header">
        <span class="map-tooltip-icon">📍</span>
        <strong>{DESA}</strong>
        <span class="map-tooltip-kec">{KECAMATAN}</span>
      </div>
      <div class="map-tooltip-body">
        ${selectedRow}
      </div>
    </div>
    `;

    polygonSeries.events.on('datavalidated', () => {
      polygonSeries.mapPolygons.each((polygon) => {
        const color = polygon.dataItem.dataContext.color;
        polygon.fill = color;
      });
    });

    polygonTemplate.stroke = am4core.color('white');
    const hs = polygonTemplate.states.create('hover');
    hs.properties.fill = am4core.color('#1565c0');

    const citySeries = map.series.push(new am4maps.MapImageSeries());
    citySeries.data = cities;
    citySeries.dataFields.value = 'size';
    const city = citySeries.mapImages.template;
    city.nonScaling = true;
    city.propertyFields.latitude = 'latitude';
    city.propertyFields.longitude = 'longitude';
    const circle = city.createChild(am4core.Circle);
    circle.fill = am4core.color('#C7D0FF');
    circle.strokeWidth = 0;
    const circleHoverState = circle.states.create('hover');
    circleHoverState.properties.strokeWidth = 1;
    circle.tooltipText = '{tooltip}';
    circle.propertyFields.radius = 'size';

    return () => {
      if (mapRef.current) {
        mapRef.current.dispose();
        mapRef.current = null;
      }
    };
  }, [mapCombine, selectedDesil]);

  const desilLabel = DESIL_OPTIONS.find((o) => o.value === selectedDesil)?.label || `Desil ${selectedDesil}`;

  const getIndividuValue = (row) => {
    if (!row) return 0;
    if (selectedDesil <= 6) {
      const key = selectedDesil === 6 ? 'Desil_6_10' : `Desil_${selectedDesil}`;
      return Number(row[key]) || 0;
    }
    return selectedDesil === 7 ? (Number(row.Desil_1) || 0) : (Number(row.Desil_1) || 0) + (Number(row.Desil_2) || 0);
  };
  const getKKValue = (row) => {
    if (!row) return 0;
    if (selectedDesil <= 6) {
      const key = selectedDesil === 6 ? 'Desil_6_10_KK' : `Desil_${selectedDesil}_KK`;
      return Number(row[key]) || 0;
    }
    return selectedDesil === 7 ? (Number(row.Desil_1_KK) || 0) : (Number(row.Desil_1_KK) || 0) + (Number(row.Desil_2_KK) || 0);
  };
  const getDisplayIndividu = (row) => {
    const val = getIndividuValue(row);
    if (selectedDesil >= 7) {
      const total = Number(row?.jumlahOrang) || 0;
      const pct = total > 0 ? ((val / total) * 100).toFixed(1) : '0';
      return `${numberWithCommas(val)} (${pct}%)`;
    }
    return numberWithCommas(val);
  };
  const getDisplayKK = (row) => {
    const val = getKKValue(row);
    if (selectedDesil >= 7) {
      const total = Number(row?.jumlahKK) || 0;
      const pct = total > 0 ? ((val / total) * 100).toFixed(1) : '0';
      return `${numberWithCommas(val)} (${pct}%)`;
    }
    return numberWithCommas(val);
  };
  const toSmallCaps = (t) => (t || '').toString().toLowerCase().replace(/(?:^|\s)\S/g, (c) => c.toUpperCase());

  const handleDownloadPdf = async () => {
    if (!mapWrapRef.current || isLoadingMap || !listDesilDesa.length) return;
    setIsDownloadingPdf(true);
    try {
      const doc = new jsPDF('p', 'mm', 'a4');
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();
      let y = 10;

      const addLogoAndSave = async () => {
        doc.setFontSize(7);
        doc.setTextColor(80, 80, 80);
        doc.text(TAGLINE, 25, y + 5, { maxWidth: pageW - 30 });
        y += 18;

        doc.setFontSize(11);
        doc.setTextColor(0, 0, 0);
        doc.text('Pilihan: ' + desilLabel, 14, y);
        y += 10;

        const mapCanvas = await html2canvas(mapWrapRef.current, {
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: '#ffffff',
        });
        const mapImgData = mapCanvas.toDataURL('image/png');
        const imgW = pageW - 28;
        const imgH = (mapCanvas.height * imgW) / mapCanvas.width;
        const maxH = pageH - y - 20;
        const finalH = Math.min(imgH, maxH);
        doc.addImage(mapImgData, 'PNG', 14, y, imgW, finalH);
        y += finalH + 12;

        doc.setFontSize(10);
        doc.setTextColor(30, 41, 59);
        doc.text('Data per Desa — ' + desilLabel, 14, y);
        y += 8;

        const tableBody = listDesilDesa.map((row, idx) => [
          String(idx + 1),
          (row.desa || '').toString(),
          (row.kecamatan || '').toString(),
          getDisplayIndividu(row),
          getDisplayKK(row),
        ]);
        doc.autoTable({
          startY: y,
          head: [['No', 'Desa', 'Kecamatan', 'Individu', 'KK']],
          body: tableBody,
          theme: 'grid',
          headStyles: { fillColor: [25, 118, 210], fontSize: 8 },
          bodyStyles: { fontSize: 7 },
          columnStyles: {
            0: { cellWidth: 12, halign: 'center' },
            1: { cellWidth: 45 },
            2: { cellWidth: 45 },
            3: { cellWidth: 40, halign: 'right' },
            4: { cellWidth: 35, halign: 'right' },
          },
          margin: { left: 14 },
          didDrawPage: (data) => {
            doc.setFontSize(8);
            doc.setTextColor(120, 120, 120);
            doc.text(`Unduh dari Tulufali - ${new Date().toLocaleDateString('id-ID')}`, 14, pageH - 8);
          },
        });

        const dateStr = new Date().toLocaleDateString('id-ID');
        doc.save(`Peta-Desil-DTSEN-${desilLabel.replace(/\s+/g, '-')}-${dateStr.replace(/\//g, '-')}.pdf`);
        setIsDownloadingPdf(false);
      };

      const img = new Image();
      img.crossOrigin = 'Anonymous';
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = img.naturalWidth;
          canvas.height = img.naturalHeight;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);
          const logoData = canvas.toDataURL('image/png');
          doc.addImage(logoData, 'PNG', 14, y, 10, 10);
          doc.setFontSize(7);
          doc.setTextColor(80, 80, 80);
          doc.text(TAGLINE, 25, y + 6, { maxWidth: pageW - 30 });
        } catch (_) {
          doc.setFontSize(7);
          doc.text(TAGLINE, 14, y + 5);
        }
        y += 18;
        addLogoAndSave();
      };
      img.onerror = () => {
        doc.setFontSize(7);
        doc.text(TAGLINE, 14, y + 5);
        y += 18;
        addLogoAndSave();
      };
      img.src = TulufaliLogo;
    } catch (err) {
      console.error('handleDownloadPdf:', err);
      setIsDownloadingPdf(false);
    }
  };

  return (
    <div className={s.mapChart}>
      <div className={s.mapWrap} ref={mapWrapRef}>
        <div className={s.mapStats}>
          <div className={s.mapStatsLeft}>
            <h6 className={s.mapStatsTitle}>SEBARAN PER DESIL (DTSEN)</h6>
            <p className={s.mapStatsYear}>{desilLabel}</p>
          </div>
          <div className={s.mapSelectWrap} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Input
              type="select"
              value={selectedDesil}
              onChange={(e) => setSelectedDesil(Number(e.target.value))}
              className={s.mapSelect}
            >
              {DESIL_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Input>
            <Button
              color="light"
              size="sm"
              onClick={handleDownloadPdf}
              disabled={isDownloadingPdf || isLoadingMap || !listDesilDesa.length}
              className={s.btnDownloadPdf}
            >
              <i className={`fa fa-file-pdf-o ${s.pdfIcon}`} aria-hidden="true" />
              {isDownloadingPdf ? ' ...' : ' PDF'}
            </Button>
          </div>
        </div>
        {isLoadingMap && (
          <div className={s.mapOverlay}>
            <div className={s.mapSpinner} />
            <span className={s.mapLoadingText}>Memuat data DTSEN...</span>
          </div>
        )}
        {!isLoadingMap && !listDesilDesa.length && (
          <div className={s.mapOverlay}>
            <span className={s.mapLoadingText}>Data DTSEN tidak tersedia</span>
          </div>
        )}
        <div className={s.map} id="mapDesil" />
        <div className={s.mapLegend}>
          <div className={s.legendItem}>
            <span className={s.legendColor} style={{ background: DESIL_COLORS.none }} />
            <span className={s.legendLabel}>Tidak ada data</span>
          </div>
          <div className={s.legendItem}>
            <span className={s.legendColor} style={{ background: DESIL_COLORS.low }} />
            <span className={s.legendLabel}>
              {thresholds.t1 > 0
                ? thresholds.isPercentMode
                  ? `0 – ${Number(thresholds.t1).toFixed(1)}%`
                  : `1 – ${thresholds.t1} orang`
                : '—'}
            </span>
          </div>
          <div className={s.legendItem}>
            <span className={s.legendColor} style={{ background: DESIL_COLORS.medium }} />
            <span className={s.legendLabel}>
              {thresholds.t1 < thresholds.t2
                ? thresholds.isPercentMode
                  ? `${Number(thresholds.t1).toFixed(1)} – ${Number(thresholds.t2).toFixed(1)}%`
                  : `${thresholds.t1 + 1} – ${thresholds.t2} orang`
                : '—'}
            </span>
          </div>
          <div className={s.legendItem}>
            <span className={s.legendColor} style={{ background: DESIL_COLORS.high }} />
            <span className={s.legendLabel}>
              {thresholds.t2 < thresholds.t3
                ? thresholds.isPercentMode
                  ? `${Number(thresholds.t2).toFixed(1)} – ${Number(thresholds.t3).toFixed(1)}%`
                  : `${thresholds.t2 + 1} – ${thresholds.t3} orang`
                : '—'}
            </span>
          </div>
          <div className={s.legendItem}>
            <span className={s.legendColor} style={{ background: DESIL_COLORS.highest }} />
            <span className={s.legendLabel}>
              {thresholds.t3 > 0
                ? thresholds.isPercentMode
                  ? `> ${Number(thresholds.t3).toFixed(1)}%`
                  : `> ${thresholds.t3} orang`
                : '—'}
            </span>
          </div>
        </div>
      </div>

      {listDesilDesa.length > 0 && (
        <div className={s.mapDataTableWrap}>
          <h6 className={s.mapDataTableTitle}>Data per Desa — {desilLabel}</h6>
          <div className={s.mapDataTableScroll}>
            <Table responsive bordered size="sm" className={s.mapDataTable}>
              <thead>
                <tr>
                  <th className={s.thNo}>No</th>
                  <th>Desa</th>
                  <th>Kecamatan</th>
                  <th className={s.thNum}>Individu</th>
                  <th className={s.thNum}>KK</th>
                </tr>
              </thead>
              <tbody>
                {listDesilDesa.map((row, idx) => (
                  <tr key={row.id || `${row.desa}-${row.kecamatan}-${idx}`}>
                    <td className={s.thNo}>{idx + 1}</td>
                    <td>{toSmallCaps(row.desa)}</td>
                    <td>{toSmallCaps(row.kecamatan)}</td>
                    <td className={s.thNum}>{getDisplayIndividu(row)}</td>
                    <td className={s.thNum}>{getDisplayKK(row)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Am4chartMapDesil;
